import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Star, Loader2, Check, CreditCard, Building2, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Datos bancarios para SPEI
const DATOS_BANCARIOS = {
  banco: "BBVA",
  titular: "Virginia Ivette Becerra Vadillo",
  clabe: "012320004892269587",
  cuenta: "0489226958"
};

// Comisión de Stripe
const STRIPE_PORCENTAJE = 0.036;
const STRIPE_FIJO = 3;

const SponsorsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paqueteSeleccionado, setPaqueteSeleccionado] = useState("");
  const [metodoPago, setMetodoPago] = useState("spei");
  const [ordenCreada, setOrdenCreada] = useState(null);
  const [copiado, setCopiado] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    nombre_negocio: "",
    giro: "",
    direccion: "",
    contacto_nombre: "",
    contacto_email: "",
    contacto_telefono: "",
    descripcion: ""
  });

  const paquetes = [
    {
      nombre: "Bigos",
      precio: 2500,
      beneficios: [
        "Logo del negocio en el Flyer del evento",
        "Logo en la página web del evento",
        "Promoción del evento en redes sociales"
      ]
    },
    {
      nombre: "Guille",
      precio: 5000,
      beneficios: [
        "Todo lo incluido en Paquete Bigos",
        "Menciones durante el evento",
        "Publicaciones dedicadas en redes sociales",
        "Destacado como patrocinador premium"
      ]
    },
    {
      nombre: "Limón",
      precio: 7500,
      beneficios: [
        "Todo lo incluido en Paquete Guille",
        "Logo en las pantallas del evento",
        "Opción de llevar lona o pancarta del negocio",
        "Espacio exclusivo para material promocional",
        "Máxima visibilidad en el evento"
      ]
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Por favor selecciona un archivo de imagen");
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const copiarAlPortapapeles = (texto, campo) => {
    navigator.clipboard.writeText(texto);
    setCopiado(campo);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopiado(""), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paqueteSeleccionado) {
      toast.error("Por favor selecciona un paquete");
      return;
    }

    if (!formData.nombre_negocio || !formData.contacto_email || !formData.contacto_nombre) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    setLoading(true);

    try {
      const paquete = paquetes.find(p => p.nombre === paqueteSeleccionado);
      
      // Si es SPEI, crear orden SPEI
      if (metodoPago === "spei") {
        const response = await axios.post(`${API}/sponsors/crear-spei`, {
          ...formData,
          paquete: paqueteSeleccionado,
          monto: paquete.precio
        });
        
        // Subir logo si existe
        if (logoFile && response.data.sponsor_id) {
          const formDataUpload = new FormData();
          formDataUpload.append("file", logoFile);
          try {
            await axios.post(
              `${API}/sponsors/${response.data.sponsor_id}/upload-logo`,
              formDataUpload,
              { headers: { "Content-Type": "multipart/form-data" } }
            );
          } catch (uploadError) {
            console.error("Error subiendo logo:", uploadError);
          }
        }
        
        setOrdenCreada({
          referencia: response.data.referencia,
          monto: paquete.precio
        });
        toast.success("Orden creada. Realiza tu transferencia SPEI.");
        setLoading(false);
        return;
      }

      // Pago con tarjeta (Stripe)
      const response = await axios.post(`${API}/sponsors/crear`, {
        ...formData,
        paquete: paqueteSeleccionado
      });
      
      const sponsorId = response.data.sponsor_id;
      
      // Si hay logo, subirlo primero
      if (logoFile) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", logoFile);
        
        try {
          await axios.post(
            `${API}/sponsors/${sponsorId}/upload-logo`,
            formDataUpload,
            {
              headers: {
                "Content-Type": "multipart/form-data"
              }
            }
          );
        } catch (uploadError) {
          console.error("Error subiendo logo:", uploadError);
        }
      }
      
      // Redirigir a Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creando sponsor:", error);
      toast.error("Error al procesar la solicitud. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-bone py-12">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
        <Button
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver al Inicio
        </Button>

        <div className="glass-card rounded-3xl p-8 md:p-12">
          <div className="flex items-center gap-4 mb-6">
            <Star className="h-12 w-12 text-zuno-wine" fill="#722F37" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-zuno-wine">
                Ser Sponsor del Evento
              </h1>
              <p className="text-lg text-slate-600 mt-2">
                Apoya nuestro evento y dale visibilidad a tu negocio
              </p>
            </div>
          </div>

          {/* Paquetes */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {paquetes.map((paquete) => (
              <Card
                key={paquete.nombre}
                className={`p-6 cursor-pointer transition-all ${
                  paqueteSeleccionado === paquete.nombre
                    ? "border-4 border-zuno-wine bg-gradient-to-br from-slate-50 to-slate-100"
                    : "border-2 border-slate-200 hover:border-zuno-wine"
                }`}
                onClick={() => setPaqueteSeleccionado(paquete.nombre)}
                data-testid={`package-${paquete.nombre.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-zuno-wine">
                    Paquete {paquete.nombre}
                  </h3>
                  {paqueteSeleccionado === paquete.nombre && (
                    <Check className="text-zuno-wine" size={28} />
                  )}
                </div>
                <div className="text-3xl font-bold text-slate-800 mb-6">
                  ${paquete.precio.toLocaleString()} MXN
                </div>
                <ul className="space-y-3">
                  {paquete.beneficios.map((beneficio, index) => (
                    <li key={`${paquete.nombre}-beneficio-${index}`} className="flex items-start gap-2">
                      <Check className="text-green-600 mt-1 flex-shrink-0" size={18} />
                      <span className="text-sm text-slate-700">{beneficio}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          {/* Formulario */}
          {paqueteSeleccionado && (
            <form onSubmit={handleSubmit} className="space-y-6 mt-8">
              <h2 className="text-2xl font-bold text-zuno-wine mb-4">
                Datos del Patrocinio
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="nombre_negocio">Nombre del Negocio *</Label>
                  <Input
                    id="nombre_negocio"
                    name="nombre_negocio"
                    value={formData.nombre_negocio}
                    onChange={handleChange}
                    placeholder="Mi Empresa S.A. de C.V."
                    required
                    data-testid="input-business-name"
                  />
                </div>

                <div>
                  <Label htmlFor="giro">Giro del Negocio *</Label>
                  <Input
                    id="giro"
                    name="giro"
                    value={formData.giro}
                    onChange={handleChange}
                    placeholder="Restaurante, Tecnología, etc."
                    required
                    data-testid="input-giro"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="direccion">Dirección del Negocio *</Label>
                  <Input
                    id="direccion"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    placeholder="Calle, Número, Colonia, Ciudad"
                    required
                    data-testid="input-direccion"
                  />
                </div>

                <div>
                  <Label htmlFor="contacto_nombre">Nombre del Contacto *</Label>
                  <Input
                    id="contacto_nombre"
                    name="contacto_nombre"
                    value={formData.contacto_nombre}
                    onChange={handleChange}
                    placeholder="Juan Pérez"
                    required
                    data-testid="input-contact-name"
                  />
                </div>

                <div>
                  <Label htmlFor="contacto_email">Email de Contacto *</Label>
                  <Input
                    id="contacto_email"
                    name="contacto_email"
                    type="email"
                    value={formData.contacto_email}
                    onChange={handleChange}
                    placeholder="contacto@empresa.com"
                    required
                    data-testid="input-contact-email"
                  />
                </div>

                <div>
                  <Label htmlFor="contacto_telefono">Teléfono de Contacto *</Label>
                  <Input
                    id="contacto_telefono"
                    name="contacto_telefono"
                    value={formData.contacto_telefono}
                    onChange={handleChange}
                    placeholder="33-1234-5678"
                    required
                    data-testid="input-contact-phone"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="descripcion">Descripción del Patrocinio (Opcional)</Label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Describe tu negocio o el mensaje que quieres que aparezca en el anuncio del evento..."
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="input-descripcion"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="logo">Logo del Negocio (Opcional)</Label>
                  <div className="mt-2">
                    <input
                      id="logo"
                      name="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="block w-full text-sm text-slate-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-zuno-wine file:text-white
                        hover:file:bg-opacity-90
                        cursor-pointer"
                      data-testid="input-logo"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      ⚠️ El logo debe tener buena resolución y sin background (fondo transparente preferible)
                    </p>
                  </div>
                  {logoPreview && (
                    <div className="mt-4">
                      <p className="text-sm font-medium mb-2">Vista Previa:</p>
                      <img
                        src={logoPreview}
                        alt="Preview"
                        className="max-h-32 object-contain border-2 border-slate-200 rounded-lg p-2"
                      />
                    </div>
                  )}
                </div>
              </div>

              <Card className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg">Paquete Seleccionado:</span>
                    <span className="text-xl font-bold text-zuno-wine">
                      {paqueteSeleccionado}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Precio del paquete:</span>
                    <span className="font-semibold">
                      ${paquetes.find(p => p.nombre === paqueteSeleccionado)?.precio.toLocaleString()} MXN
                    </span>
                  </div>
                  {metodoPago === "tarjeta" && (
                    <div className="flex justify-between items-center text-slate-600">
                      <span>Comisión procesamiento (3.6% + $3):</span>
                      <span>
                        ${Math.round((paquetes.find(p => p.nombre === paqueteSeleccionado)?.precio || 0) * STRIPE_PORCENTAJE + STRIPE_FIJO)} MXN
                      </span>
                    </div>
                  )}
                  <div className="border-t-2 border-zuno-wine pt-3 flex justify-between items-center">
                    <span className="text-xl font-bold text-zuno-wine">Total:</span>
                    <span className="text-2xl font-bold text-zuno-wine">
                      ${metodoPago === "tarjeta" 
                        ? Math.round((paquetes.find(p => p.nombre === paqueteSeleccionado)?.precio || 0) * (1 + STRIPE_PORCENTAJE) + STRIPE_FIJO).toLocaleString()
                        : paquetes.find(p => p.nombre === paqueteSeleccionado)?.precio.toLocaleString()
                      } MXN
                    </span>
                  </div>
                </div>
              </Card>

              {/* Selector de método de pago */}
              {!ordenCreada && (
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Método de Pago</Label>
                  <div className="flex justify-center">
                    <Button
                      type="button"
                      onClick={() => setMetodoPago("spei")}
                      variant="default"
                      className="h-auto py-4 flex flex-col items-center gap-2 bg-zuno-wine text-white w-full max-w-sm"
                    >
                      <Building2 size={28} />
                      <span className="font-semibold">Transferencia SPEI</span>
                      <span className="text-xs opacity-80">Sin comisión adicional</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Datos bancarios si se creó orden SPEI */}
              {ordenCreada && (
                <Card className="p-6 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="text-green-600" size={28} />
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">¡Orden Creada!</h3>
                      <p className="text-sm text-slate-600">Referencia: <strong>{ordenCreada.referencia}</strong></p>
                    </div>
                  </div>
                  
                  <p className="text-slate-700 mb-4">
                    Realiza tu transferencia SPEI con los siguientes datos:
                  </p>

                  <div className="bg-white rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Banco:</span>
                      <span className="font-semibold">{DATOS_BANCARIOS.banco}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Titular:</span>
                      <span className="font-semibold">{DATOS_BANCARIOS.titular}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">No. Cuenta:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">{DATOS_BANCARIOS.cuenta}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => copiarAlPortapapeles(DATOS_BANCARIOS.cuenta, "cuenta")}
                        >
                          {copiado === "cuenta" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">CLABE:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">{DATOS_BANCARIOS.clabe}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => copiarAlPortapapeles(DATOS_BANCARIOS.clabe, "clabe")}
                        >
                          {copiado === "clabe" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center border-t pt-3 mt-3">
                      <span className="text-slate-600">Monto:</span>
                      <span className="text-2xl font-bold text-zuno-wine">${ordenCreada.monto.toLocaleString()} MXN</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Concepto:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-zuno-wine">{ordenCreada.referencia}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => copiarAlPortapapeles(ordenCreada.referencia, "ref")}
                        >
                          {copiado === "ref" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={() => navigate("/")}
                    className="w-full mt-4 btn-primary"
                  >
                    Volver al Inicio
                  </Button>
                </Card>
              )}

              {!ordenCreada && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      📩 Nota: Después de completar el pago, recibirás un correo electrónico de confirmación.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary text-xl py-6"
                    disabled={loading}
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <Building2 className="mr-2" />
                        Generar Orden SPEI
                      </>
                    )}
                  </Button>
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SponsorsPage;
